import { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { Header } from "../../components";
import { useAuthContext } from "../../context/auth";
import { getUsers, getContactosCurados, getBlacklistParaEnvio, getRebotesObsoletos } from "../../utils";
import styles from "./stylesMailing.module.scss";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const FUNCTION_URL = `${SUPABASE_URL}/functions/v1/email-blast`;

const ESTADOS = { PENDIENTE: "pendiente", ENVIANDO: "enviando", COMPLETADO: "completado", ERROR: "error" };

const MailingPage = () => {
  const { id, pais } = useAuthContext();
  const paisCode = pais === "Perú" ? "PER" : "CHI";

  const [drafts, setDrafts] = useState([]);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [loadingDrafts, setLoadingDrafts] = useState(false);

  const [destinatarios, setDestinatarios] = useState("");
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [emailsToSend, setEmailsToSend] = useState([]);

  // Contactos
  const [contactos, setContactos] = useState([]);
  const [loadingContactos, setLoadingContactos] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState({
    agencia: true,
    cliente: true,
    edificio: false,
    contacto: true,
  });
  // Emails descartados en la curación: se restan siempre, no son seleccionables.
  const [emailsDescartados, setEmailsDescartados] = useState([]);
  // Blacklist del país activo (email exacto + dominio): también se resta siempre.
  const [blacklistEmails, setBlacklistEmails] = useState([]);
  const [blacklistDominios, setBlacklistDominios] = useState([]);
  // Rebotes marcados como obsoletos en la curación: también se restan siempre.
  const [emailsRebotados, setEmailsRebotados] = useState([]);

  // Campaña activa y polling
  const [campaign, setCampaign] = useState(null);
  const pollingRef = useRef(null);

  const cargarBorradores = useCallback(async () => {
    setLoadingDrafts(true);
    try {
      const url = `${FUNCTION_URL}?action=drafts&usuario_id=${id}&pais=${paisCode}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.drafts) setDrafts(data.drafts);
    } catch {
      toast.error("Error al cargar borradores");
    } finally {
      setLoadingDrafts(false);
    }
  }, [id, paisCode]);

  const cargarContactos = useCallback(async () => {
    setLoadingContactos(true);
    try {
      const pc = pais === "Perú" ? "PER" : "CHI";
      const [allUsuarios, curados, blacklist, rebotados] = await Promise.all([
        getUsers(),
        getContactosCurados({ pais: pc }),
        getBlacklistParaEnvio({ pais: pc }),
        getRebotesObsoletos({ pais: pc }),
      ]);

      const contactosList = allUsuarios
        .filter(
          (u) =>
            u.pais === pc &&
            u.email?.includes("@") &&
            ["agencia", "cliente", "edificio"].includes(u.rol) &&
            u.habilitado !== false
        )
        .map((u) => ({
          id: u.id,
          nombre: `${u.nombre || ""} ${u.apellido || ""}`.trim(),
          email: u.email,
          tipo: u.rol,
        }));

      // Los solo_mailing entran como una categoría más ("contacto"), así el
      // filtro por checkbox de más abajo los trata igual que a los roles.
      const soloMailingList = curados.soloMailing.map((c) => ({
        id: `contacto-${c.email}`,
        nombre: c.nombre,
        email: c.email,
        tipo: "contacto",
      }));

      setContactos([...contactosList, ...soloMailingList]);
      setEmailsDescartados(curados.descartados);
      setBlacklistEmails(blacklist.emails);
      setBlacklistDominios(blacklist.dominios);
      setEmailsRebotados(rebotados.emails);
    } catch {
      toast.error("Error al cargar contactos");
    } finally {
      setLoadingContactos(false);
    }
  }, [pais]);

  // Cargar campaña activa al entrar
  const cargarCampanaActiva = useCallback(async () => {
    try {
      const res = await fetch(`${FUNCTION_URL}?action=status&usuario_id=${id}`);
      const data = await res.json();
      const activa = data.campaigns?.find((c) => c.estado === ESTADOS.ENVIANDO || c.estado === ESTADOS.PENDIENTE);
      if (activa) {
        setCampaign(activa);
      }
    } catch {
      // silencioso
    }
  }, [id]);

  useEffect(() => {
    cargarBorradores();
    cargarContactos();
    cargarCampanaActiva();
  }, [cargarBorradores, cargarContactos, cargarCampanaActiva]);

  // Polling: consultar progreso cada 10 segundos cuando hay campaña activa
  useEffect(() => {
    if (campaign && (campaign.estado === ESTADOS.ENVIANDO || campaign.estado === ESTADOS.PENDIENTE)) {
      pollingRef.current = setInterval(async () => {
        try {
          const res = await fetch(`${FUNCTION_URL}?action=status&campaign_id=${campaign.id}`);
          const data = await res.json();
          if (data.campaign) {
            setCampaign(data.campaign);
            if (data.campaign.estado === ESTADOS.COMPLETADO) {
              toast.success(
                `Mailing completado: ${data.campaign.enviados}/${data.campaign.total_destinatarios} enviados`
              );
              clearInterval(pollingRef.current);
            }
          }
        } catch {
          // silencioso
        }
      }, 10000);

      return () => clearInterval(pollingRef.current);
    }
  }, [campaign?.id, campaign?.estado]);

  const contactosFiltrados = contactos.filter((c) => selectedRoles[c.tipo]);

  // Exclusión dura: un descartado o blacklisteado no entra aunque además exista
  // como usuario habilitado o solo_mailing con su rol tildado. Se aplica después
  // de los checkboxes, sobre el email normalizado, para que no dependa de por qué
  // categoría entró. Blacklist bloquea por email exacto y por dominio.
  const descartadosSet = new Set(emailsDescartados);
  const blacklistEmailsSet = new Set(blacklistEmails);
  const blacklistDominiosSet = new Set(blacklistDominios);
  const rebotadosSet = new Set(emailsRebotados);
  const estaBloqueado = (email) =>
    descartadosSet.has(email) ||
    blacklistEmailsSet.has(email) ||
    blacklistDominiosSet.has(email.split("@")[1] || "") ||
    rebotadosSet.has(email);

  const emailsSeleccionados = contactosFiltrados.map((c) => c.email.toLowerCase());
  const emailsUnicosSet = [...new Set(emailsSeleccionados)];
  const emailsUnicos = emailsUnicosSet.filter((e) => !estaBloqueado(e));
  const excluidosPorBloqueo = emailsUnicosSet.length - emailsUnicos.length;

  const handleCargarContactos = () => {
    setDestinatarios(emailsUnicos.join("\n"));
    toast.success(`${emailsUnicos.length} contactos cargados`);
  };

  const handleEnviar = async () => {
    if (!selectedDraft) {
      toast.error("Seleccioná un borrador primero");
      return;
    }

    const emails = destinatarios
      .split(/[\n,;]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e && e.includes("@"));

    if (emails.length === 0) {
      toast.error("No hay destinatarios válidos");
      return;
    }

    setEmailsToSend(emails);
    setShowConfirm(true);
  };

  const handleConfirmEnviar = async () => {
    setShowConfirm(false);
    setSending(true);

    try {
      const res = await fetch(FUNCTION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          usuario_id: id,
          pais: paisCode,
          draft_id: selectedDraft.id,
          subject: selectedDraft.subject,
          destinatarios: emailsToSend,
        }),
      });

      const data = await res.json();

      if (data.success) {
        toast.success(`Campaña encolada: ${emailsToSend.length} destinatarios`);
        setCampaign({
          id: data.campaign_id,
          subject: selectedDraft.subject,
          total_destinatarios: data.total_destinatarios,
          enviados: 0,
          errores: 0,
          estado: ESTADOS.PENDIENTE,
        });
        setDestinatarios("");
        setSelectedDraft(null);
      } else {
        toast.error(data.error || "Error al encolar campaña");
      }
    } catch {
      toast.error("Error de conexión");
    } finally {
      setSending(false);
    }
  };

  const porcentaje = campaign?.total_destinatarios
    ? Math.round((campaign.enviados / campaign.total_destinatarios) * 100)
    : 0;

  return (
    <div className={styles.wrapper}>
      <Header leftContent={<h2>Mailing</h2>} />

      {/* Barra de progreso de campaña activa */}
      {campaign && campaign.estado !== ESTADOS.COMPLETADO && (
        <div className={styles.campaignBar}>
          <div className={styles.campaignInfo}>
            <strong>{campaign.subject}</strong>
            <span>
              {campaign.enviados}/{campaign.total_destinatarios} enviados
              {campaign.errores > 0 && ` | ${campaign.errores} errores`}
            </span>
            <span className={styles.campaignEstado}>
              {campaign.estado === ESTADOS.PENDIENTE && "En cola - se enviará entre 08:00 y 17:00"}
              {campaign.estado === ESTADOS.ENVIANDO && "Enviando..."}
            </span>
          </div>
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{ width: `${porcentaje}%` }} />
          </div>
          <span className={styles.progressPct}>{porcentaje}%</span>
        </div>
      )}

      {/* Campaña completada */}
      {campaign && campaign.estado === ESTADOS.COMPLETADO && (
        <div className={styles.campaignDone}>
          <strong>{campaign.subject}</strong> - Completado: {campaign.enviados}/{campaign.total_destinatarios} enviados
          {campaign.errores > 0 && ` | ${campaign.errores} errores`}
          <button className={styles.btnDismiss} onClick={() => setCampaign(null)}>
            Cerrar
          </button>
        </div>
      )}

      <div className={styles.content}>
        {/* Columna izquierda: Borrador */}
        <div className={styles.panel}>
          <h3>1. Seleccioná un borrador de Gmail</h3>
          <button className={styles.btnRefresh} onClick={cargarBorradores} disabled={loadingDrafts}>
            {loadingDrafts ? "Cargando..." : "Actualizar borradores"}
          </button>

          <div className={styles.draftList}>
            {drafts.map((draft) => (
              <div
                key={draft.id}
                className={`${styles.draftItem} ${selectedDraft?.id === draft.id ? styles.selected : ""}`}
                onClick={() => setSelectedDraft(draft)}
              >
                <strong>{draft.subject}</strong>
                <p>{draft.snippet}</p>
              </div>
            ))}
            {drafts.length === 0 && !loadingDrafts && <p className={styles.empty}>No hay borradores en Gmail</p>}
          </div>
        </div>

        {/* Columna derecha: Destinatarios */}
        <div className={styles.panel}>
          <h3>2. Destinatarios</h3>

          <div className={styles.filtros}>
            <label className={styles.checkLabel}>
              <input
                type="checkbox"
                checked={selectedRoles.agencia}
                onChange={(e) => setSelectedRoles((prev) => ({ ...prev, agencia: e.target.checked }))}
              />
              Agencia ({contactos.filter((c) => c.tipo === "agencia").length})
            </label>
            <label className={styles.checkLabel}>
              <input
                type="checkbox"
                checked={selectedRoles.cliente}
                onChange={(e) => setSelectedRoles((prev) => ({ ...prev, cliente: e.target.checked }))}
              />
              Cliente ({contactos.filter((c) => c.tipo === "cliente").length})
            </label>
            <label className={styles.checkLabel}>
              <input
                type="checkbox"
                checked={selectedRoles.edificio}
                onChange={(e) => setSelectedRoles((prev) => ({ ...prev, edificio: e.target.checked }))}
              />
              Edificio ({contactos.filter((c) => c.tipo === "edificio").length})
            </label>
            <label className={styles.checkLabel} title="Contactos marcados como 'solo mailing' en la curación">
              <input
                type="checkbox"
                checked={selectedRoles.contacto}
                onChange={(e) => setSelectedRoles((prev) => ({ ...prev, contacto: e.target.checked }))}
              />
              Mailing ({contactos.filter((c) => c.tipo === "contacto").length})
            </label>
          </div>

          {excluidosPorBloqueo > 0 && (
            <p className={styles.count}>
              {excluidosPorBloqueo} excluido{excluidosPorBloqueo === 1 ? "" : "s"} por descarte, blacklist o rebote
            </p>
          )}

          <button className={styles.btnCargar} onClick={handleCargarContactos} disabled={loadingContactos}>
            {loadingContactos ? "Cargando..." : `Cargar ${emailsUnicos.length} contactos`}
          </button>

          <textarea
            className={styles.textarea}
            value={destinatarios}
            onChange={(e) => setDestinatarios(e.target.value)}
            placeholder="Pegá emails (uno por línea) o usá los botones de arriba"
            rows={12}
          />

          <p className={styles.count}>
            {destinatarios.split(/[\n,;]+/).filter((e) => e.trim() && e.includes("@")).length} emails
          </p>
        </div>
      </div>

      {/* Botón enviar */}
      <div className={styles.footer}>
        {selectedDraft && (
          <div className={styles.resumen}>
            Borrador: <strong>{selectedDraft.subject}</strong>
          </div>
        )}

        <button className={styles.btnEnviar} onClick={handleEnviar} disabled={sending || !selectedDraft}>
          {sending ? "Encolando..." : "Enviar Mailing"}
        </button>
      </div>

      {/* Modal de confirmación */}
      {showConfirm && (
        <div className={styles.modalOverlay} onClick={() => setShowConfirm(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Confirmar envío</h3>
            <p>
              ¿Enviar <strong>{selectedDraft?.subject}</strong> a <strong>{emailsToSend.length}</strong> destinatarios?
            </p>
            <p className={styles.modalHorario}>Se enviará automáticamente entre 08:00 y 17:00 hrs.</p>
            <div className={styles.modalButtons}>
              <button className={styles.btnCancelar} onClick={() => setShowConfirm(false)}>
                Cancelar
              </button>
              <button className={styles.btnConfirmar} onClick={handleConfirmEnviar}>
                Confirmar envío
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailingPage;
